/**
 * "Yeni Stok Ekle" tıklanınca çıkan seçim adımı.
 *
 * Tek işi kullanıcıyı iki yoldan birine göndermek: sıfırdan ürün tanımlamak mı, yoksa
 * ortak katalogdan hazır ürün çekmek mi. Bu ayrım olmadan katalog akışı ya ürün ekleme
 * modalının içine gömülecekti ya da ayrı bir butona ihtiyaç duyacaktı.
 */

import React from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import MenuBookIcon from '@mui/icons-material/MenuBook';

export interface AddStockChoiceModalProps {
  open: boolean;
  onClose: () => void;
  onChooseNewProduct: () => void;
  onChooseFromCatalog: () => void;
}

interface ChoiceCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
}

const ChoiceCard: React.FC<ChoiceCardProps> = ({ icon, title, description, onClick }) => (
  <Paper
    variant="outlined"
    onClick={onClick}
    sx={{
      flex: 1,
      p: 3,
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      gap: 1,
      transition: 'border-color 120ms, background-color 120ms, transform 120ms',
      '&:hover': {
        borderColor: 'primary.main',
        bgcolor: 'action.hover',
        transform: 'translateY(-2px)',
      },
    }}
  >
    <Box
      sx={{
        width: 56,
        height: 56,
        borderRadius: '50%',
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 0.5,
      }}
    >
      {icon}
    </Box>
    <Typography variant="subtitle1" fontWeight={700}>
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      {description}
    </Typography>
  </Paper>
);

const AddStockChoiceModal: React.FC<AddStockChoiceModalProps> = ({
  open,
  onClose,
  onChooseNewProduct,
  onChooseFromCatalog,
}) => (
  <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
    <DialogTitle sx={{ display: 'flex', alignItems: 'center', pr: 1 }}>
      <Typography variant="h6" component="span" sx={{ flex: 1, fontWeight: 700 }}>
        Yeni Stok Ekle
      </Typography>
      <IconButton size="small" onClick={onClose} aria-label="Kapat">
        <CloseIcon />
      </IconButton>
    </DialogTitle>

    <DialogContent dividers>
      <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
        <ChoiceCard
          icon={<AddIcon fontSize="large" />}
          title="+ Yeni Ürün"
          description="Ürünü sıfırdan kendin tanımla: ad, barkod, fiyat ve diğer tüm alanlar."
          onClick={onChooseNewProduct}
        />
        <ChoiceCard
          icon={<MenuBookIcon fontSize="large" />}
          title="+ Katalogdan Ekle"
          description="Ortak katalogdan hazır ürünleri seç, tek tek veya kategori kategori stoğuna aktar."
          onClick={onChooseFromCatalog}
        />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
        <Inventory2Icon fontSize="small" color="disabled" />
        <Typography variant="caption" color="text.secondary">
          Katalogdan aktarılan ürünler 0 fiyatla gelir; satış fiyatlarını sonra
          düzenleyebilirsin.
        </Typography>
      </Box>
    </DialogContent>
  </Dialog>
);

export default AddStockChoiceModal;
